<template>
  <section class="attachment-popup-body" v-loading="loading">
    <div
      class="attachment-upload-button"
      v-if="!noteDialogVisible && hasPermission('cases', 'delete')"
    >
      <el-button
        @click="onAddNote"
        class="new-note-button"
      >
        <el-icon><Notebook /></el-icon>
        NEW NOTE
      </el-button>
    </div>

    <div
      v-if="!noNotes && !isEdit && !noteDialogVisible"
      class="attachment-uploaded"
    >
      <ul>
        <li v-for="(note, index) of allNotes" :key="index">
          <div class="content">
            <div class="icon">
              <img
                src="@/assets/images/icons/attachments/internal-case-notes_1.svg"
                alt="icon"
                class="img-fluid"
              />
            </div>
            <div class="title">
              <h4>{{ note.title }}</h4>
              <p>{{ note.description }}</p>
            </div>
          </div>
          <div class="notes-btn-block">
            <el-button class="notes-btn" @click="onEdit(note)" v-if="hasPermission('cases', 'delete')">
              <img
                class="action-active"
                src="@/assets/images/icons/actions/edit-active.svg"
                alt="icon"
              />
            </el-button>
            <el-button class="notes-btn" @click="onDelete(note)" v-if="hasPermission('cases', 'delete')">
              <img
                class="action-active"
                src="@/assets/images/icons/actions/delete-active.svg"
                alt="icon"
              />
            </el-button>
          </div>
        </li>
      </ul>
    </div>
    <div class="add-notes-form">
      <el-form class="vue-form" v-model="noteForm" v-if="noteDialogVisible">
        <el-form-item>
          <label for="">Title <span class="star">*</span></label>

          <el-input v-model="noteForm.title" placeholder="Title"></el-input>
          <p
            class="error"
            v-if="getNoteValidationErrors && getNoteValidationErrors.title"
          >
            {{ getNoteValidationErrors.title }}
          </p>
        </el-form-item>
        <el-form-item>
          <label for="">Description</label>
          <el-input
            v-model="noteForm.description"
            type="textarea"
            placeholder="Description"
            rows="10"
          ></el-input>
          <p
            class="error"
            v-if="
              getNoteValidationErrors && getNoteValidationErrors.description
            "
          >
            {{ getNoteValidationErrors.description }}
          </p>
        </el-form-item>
        <ul class="form-actions">
          <li>
            <el-button
              @click="submitForm"
              :disabled="checkReason"
              class="add-note-button"
            >
              <span class="el-icon-circle-plus-outline"></span>
              {{ isEdit ? "Update" : "Add" }} Note
            </el-button>
          </li>
          <li>
            <el-button @click="cancelForm" class="cancel-button">
              <span class="el-icon-circle-plus-outline"></span>
              Cancel
            </el-button>
          </li>
        </ul>
      </el-form>
    </div>
    <div class="attachments-empty" v-if="noNotes && !noteDialogVisible">
      <p>You can manage case notes here</p>
    </div>
    <div class="mini-reason-for-change" v-if="isEdit">
      <ResonForChange
        v-if="takeReasonForChangeDialog"
        v-on:close="takeReasonForChangeDialog = false"
        v-on:reasonSubmit="onTakeAction"
        title="Reason for Update"
      ></ResonForChange>
    </div>
    <div class="mini-reason-for-change" v-else>
      <ResonForChange
        v-if="takeReasonForChangeDialog"
        v-on:close="takeReasonForChangeDialog = false"
        v-on:reasonSubmit="onTakeAction"
        title="Reason for Remove"
      ></ResonForChange>
    </div>
  </section>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { mapGetters } from "vuex";
const ResonForChange = defineAsyncComponent(() =>
  import("@/components/misc/ResonForChange")
);
import moment from "moment";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";
export default {
  name: "InternalCaseNotes",
  components: {
    ResonForChange,
  },
  mixins:[UserPermissionsHelper],
  mounted() {
    this.checkCaseInformation();
  },
  computed: {
    ...mapGetters("cases", [
      "getNoteAddStatus",
      "getNoteUpdateStatus",
      "getNoteDeleteStatus",
      "getCaseInformation",
      "getNoteValidationErrors",
    ]),
    isCaseCompleted() {
      if (!this.getCaseInformation) {
        return false;
      }
      return this.getCaseInformation.status === "COMPLETE";
    },
    editButtonTitle() {
      if (this.isCaseCompleted) {
        return "Can't edit the completed Case";
      }

      return "";
    },
    checkReason() {
      if (this.noteForm.title && this.noteForm.title.length >= 5) {
        return false;
      }
      if (this.noteForm.description && this.noteForm.description.length >= 5) {
        return false;
      }
      return true;
    },
  },
  data() {
    return {
      allNotes: [],
      caseId: "",
      loading: false,
      noNotes: false,
      isEdit: false,
      noteForm: {
        title: "",
        description: "",
      },

      selectedNote: {
        title: "",
        id: "",
        description: "",
      },
      disable: false,
      noteDialogVisible: false,
      takeReasonForChangeDialog: false,
    };
  },
  methods: {
    async checkCaseInformation() {
      try {
        this.loading = true;
        this.caseId = this.$route.params.case_id;
        if (!this.caseId) {
          this.loading = false;
          this.$router.push({
            to: "/cases",
          });
        } else {
          await this.$store.dispatch("cases/fetchCaseInformation", this.caseId);
          if (!this.getCaseInformation) {
            this.disablePatientInformation = true;
            this.showTertiaryInsuranceForm = true;
          }

          if (
            this.getCaseInformation &&
            this.getCaseInformation.internal_notes
          ) {
            this.allNotes = this.getCaseInformation.internal_notes || [];
            this.allNotes = this.allNotes.sort(
              (a, b) => moment(b.created_at) - moment(a.created_at)
            );
            this.noNotes = this.allNotes.length ? false : true;
          }
          this.loading = false;
        }
      } catch (error) {
        console.log(error);
      }
    },
    onEdit(note) {
      this.$store.dispatch("cases/unsetFormValidationErrors");
      this.selectedNote = note;
      this.noteForm.title = note.title;
      this.noteForm.description = note.description;
      this.noteDialogVisible = true;
      this.isEdit = true;
    },

    onAddNote() {
      this.$store.dispatch("cases/unsetFormValidationErrors");
      this.noteForm.title = "";
      this.noteForm.description = "";
      this.selectedNote = null;
      this.noteDialogVisible = true;
      this.isEdit = false;
    },

    onTakeAction(reason) {
      if (this.isEdit) {
        this.updateNote(reason);
      } else {
        this.deleteNote(reason);
      }
    },

    submitForm() {
      if (this.selectedNote && this.selectedNote._id) {
        this.takeReasonForChangeDialog = true;
      } else {
        this.addNote();
      }
    },
    cancelForm() {
      this.noteForm.title = "";
      this.noteForm.description = "";
      this.selectedNote = null;
      this.noteDialogVisible = false;
      this.isEdit = false;
    },
    async addNote() {
      this.loading = true;
      this.disable = true;
      let params = {
        title: this.noteForm.title,
        description: this.noteForm.description,
        case_id: this.caseId,
      };
      await this.$store.dispatch("cases/addNote", params);
      if (this.getNoteAddStatus) {
        successNotification("Note added successfully");
        this.noteDialogVisible = false;
        this.checkCaseInformation();
      }
      this.disable = false;
      this.loading = false;
    },

    async updateNote(reason) {
      let params = {
        title: this.noteForm.title,
        description: this.noteForm.description,
        case_id: this.caseId,
        id: this.selectedNote._id,
        data_update_reason: reason,
      };
      params.case_id = this.caseId;
      await this.$store.dispatch("cases/updateNote", params);
      if (this.getNoteUpdateStatus) {
        successNotification("Note updated successfully");
        this.noteDialogVisible = false;
        this.isEdit = false;
        this.checkCaseInformation();
      }
    },
    async onDelete(note) {
      this.takeReasonForChangeDialog = true;
      this.selectedNote = note;
      this.isEdit = false;
    },

    async deleteNote(reason) {
      let params = {
        case_id: this.caseId,
        id: this.selectedNote._id,
        data_update_reason: reason,
      };
      await this.$store.dispatch("cases/deleteNote", params);
      if (this.getNoteDeleteStatus) {
        successNotification("Note deleted successfully");
        this.checkCaseInformation();
      }
    },
  },
};
</script>
<style lang="scss">
.mini-reason-for-change {
  .el-dialog {
    width: 28%;
    height: 210px !important;
    margin-top: 20vh;
  }
}
</style>
